import { selectableOrderStatuses } from '@moko/utils';
import { OrderSiteAvailability } from '@moko/ui';
import {
  siteAvailabilityRequests,
  type SiteAvailabilityDraft,
} from '@moko/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  IMetadata,
  getMetadata,
  getEntitiesMetadatas,
} from '../../services/metadataService';
import { validate, type IValidationError } from '@moko/utils';
import {
  ICreateOrderRequest,
  IUpdateOrderRequest,
  createOrderedEntity,
  createOrder,
  editOrder,
  getOrder,
} from '../../services/orderService';
import {
  getOrderStatuses,
  type IOrderStatus,
} from '../../services/workService';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import { useLocation } from 'react-router-dom';
import OtherInfo from '../../common/OtherInfoPortal';
import {
  EditItem,
  WorkTypeField,
  useTenantWorkTypes,
  useWorkAssetForms,
} from '@moko/ui';
import { getTenantIdFromToken } from '@moko/utils/src/tenantHelper';
import { MessageBar, MessageBarBody, Button } from '@fluentui/react-components';
import {
  FontIcon,
  IButtonProps,
  Pivot,
  PivotItem,
  TooltipHost,
} from '@fluentui/react';
import OrderAssets from './OrderAssets';
import OrderBilling from './OrderBilling';
import {
  hasCodeConventionFormat,
  tenantInfoAtom,
} from '../../atoms/tenantAtom';
import { getTenantWorkTypes } from '../../services/settingsService';

const textOrNull = (value: unknown): string | null => {
  const text = typeof value === 'string' ? value.trim() : '';
  return text || null;
};

const Order = () => {
  const [siteAvailabilityWindows, setSiteAvailabilityWindows] = useState<
    SiteAvailabilityDraft[]
  >([]);
  const query = new URLSearchParams(useLocation().search);
  const routeOrderId = Number.parseInt(query.get('id') ?? '');

  const [metadata, setMetadata] = useState<IMetadata>();
  const [currentOrderId, setCurrentOrderId] = useState<number | undefined>(
    Number.isNaN(routeOrderId) ? undefined : routeOrderId
  );
  const initialRelatedAssetIdsRef = useRef<number[]>([]);
  const [relatedAssetIds, setRelatedAssetIds] = useState<number[]>([]);
  const [allEntitiesMetadatas, setAllEntitiesMetadatas] = useState<
    { key: string; value: string }[]
  >([]);
  const [orderedEntitiesMetadatas, setOrderedEntitiesMetadatas] = useState<
    { key: string; value: string }[]
  >([]);
  const [metadatas, setMetadatas] = useState<{ key: string; value: string }[]>(
    []
  );
  const [metadataName, setMetadataName] = useState<string>('');
  const [otherInfo, setOtherInfo] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<IValidationError[]>();
  const companyInfoErrors = useMemo(
    () => validate(otherInfo, metadata?.validations ?? [], metadata?.fields),
    [otherInfo, metadata]
  );
  useEffect(() => {
    setErrors(companyInfoErrors);
  }, [companyInfoErrors]);
  const [savedStatusId, setSavedStatusId] = useState<number>();
  const [orderStatuses, setOrderStatuses] = useState<IOrderStatus[]>([]);
  const [orderedEntityType, setOrderedEntityType] = useState('');
  const [workFormDefinitionId, setWorkFormDefinitionId] = useState<number>();
  const [workMetadataId, setWorkMetadataId] = useState<number>();
  const savedWorkForm = useRef<{ key?: string; metadataId?: number }>({});
  const workVersions = useRef<Record<string, number | undefined>>({});
  const [orderTenantId, setOrderTenantId] = useState<number | undefined>(
    () => getTenantIdFromToken() ?? undefined
  );
  const formIdentities = useRef<Record<string, number | undefined>>({});
  const [knownAssetForms, setKnownAssetForms] = useState<
    Record<number, number>
  >({});
  const assetForms = useWorkAssetForms({
    enabled:
      orderedEntityType === 'Work' || (!!currentOrderId && !orderedEntityType),
    workFormDefinitionId,
    tenantId: orderTenantId,
    assetIds: relatedAssetIds,
    creates: [],
    imports: [],
    knownAssetForms,
  });
  const [workTypeId, setWorkTypeId] = useState<number | null>(null);
  const [savedWorkTypeId, setSavedWorkTypeId] = useState<number | null>(null);
  const [workTypeReady, setWorkTypeReady] = useState(
    Number.isNaN(routeOrderId)
  );
  const workTypes = useTenantWorkTypes(undefined, true, getTenantWorkTypes);

  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);

  const isSaved = useRef(false);
  const hasDraftRelatedAssetsChanges =
    !currentOrderId &&
    relatedAssetIds.join(',') !== initialRelatedAssetIdsRef.current.join(',');
  const isAssetsTabDisabled = !currentOrderId;
  const assetsTabTooltip = 'Assets can be linked after the order is saved.';
  const disabledAssetsTabProps: IButtonProps | undefined = isAssetsTabDisabled
    ? {
        disabled: true,
        style: { color: 'grey' },
      }
    : undefined;

  const filterOrderedMetadatas = (
    items: { key: string; value: string }[],
    entityType?: string | null
  ) => {
    if (!entityType) {
      return [];
    }
    const prefix = `${entityType}|`.toLowerCase();
    return items.filter((em) => em.key.toLowerCase().startsWith(prefix));
  };

  const fetchData = async () => {
    const abortController = new AbortController();
    setIsInProgress(true);
    try {
      const savedOrder = routeOrderId
        ? await getOrder(abortController, routeOrderId)
        : undefined;
      const tenantId = savedOrder?.tenantId ?? orderTenantId;
      const entityMetadatas = await getEntitiesMetadatas(
        abortController,
        ['Work', 'Asset', 'Order'],
        tenantId
      );
      const allEntitiesMetadatas = Object.keys(
        entityMetadatas?.metadatas || {}
      ).map((key) => ({ key: key, value: key.split('|')[1] }));
      formIdentities.current = Object.fromEntries(
        await Promise.all(
          Object.entries(entityMetadatas?.metadatas ?? {}).map(
            async ([key, metadata]) => {
              if (!key.startsWith('Work|'))
                return [key, metadata.formDefinitionId] as const;
              const form = await getMetadata(
                abortController,
                'Work',
                true,
                key.slice('Work|'.length),
                tenantId
              );
              workVersions.current[key] = form.id;
              return [key, form.formDefinitionId] as const;
            }
          )
        )
      );
      setAllEntitiesMetadatas(allEntitiesMetadatas);
      const metadatas =
        allEntitiesMetadatas?.filter((em) => em.key.startsWith('Order|')) ?? [];
      setMetadatas(metadatas);
      setOrderStatuses(await getOrderStatuses(abortController));
      if (savedOrder) {
        setOrderedEntitiesMetadatas(
          filterOrderedMetadatas(
            allEntitiesMetadatas ?? [],
            savedOrder.entityType ?? savedOrder.orderedEntityType
          )
        );
      }
    } catch (error: any) {
      console.error('Error:', error);
      setErrorMessage(error.message);
    } finally {
      setIsInProgress(false);
    }
    return () => {
      abortController.abort();
    };
  };

  useEffect(() => {
    fetchData();
  }, []);

  const tenantInfo = useAtomValue(tenantInfoAtom);
  const hasCodeConvention = hasCodeConventionFormat(
    tenantInfo,
    'order-code-convention'
  );
  const isEditMode = !!currentOrderId;

  return (
    <>
      <Pivot>
        <PivotItem headerText="Details">
          {!assetForms.canProceed && (
            <MessageBar
              intent={
                assetForms.error || assetForms.issues.length ? 'error' : 'info'
              }
            >
              <MessageBarBody>
                {assetForms.message}
                <Button onClick={assetForms.retry}>Retry</Button>
              </MessageBarBody>
            </MessageBar>
          )}
          <EditItem
            headerSlot={
              (isEditMode || orderedEntityType === 'Work') && (
                <WorkTypeField
                  mode={isEditMode ? 'read' : 'create'}
                  options={workTypes.options}
                  value={isEditMode ? savedWorkTypeId : workTypeId}
                  onChange={setWorkTypeId}
                  loading={workTypes.loading || (isEditMode && !workTypeReady)}
                  error={workTypes.error ? 'Could not load work types.' : null}
                  onRetry={workTypes.refetch}
                />
              )
            }
            getAction={async (abortController, id) => {
              const order: any = await getOrder(abortController, id);
              setCurrentOrderId(id);
              const ids = (order.relatedAssets ?? []).map((a: any) => a.id);
              setRelatedAssetIds(ids);
              initialRelatedAssetIdsRef.current = ids;
              setOtherInfo(order.otherInfo);
              setSavedStatusId(
                order.statusId == null ? undefined : Number(order.statusId)
              );
              order.statusId = (order.statusId ?? '').toString();
              const metadataName = order.metadataName
                ? order.metadataName.split('|')[1]
                : undefined;
              setMetadataName(metadataName);
              const metadata = await getMetadata(
                abortController,
                'Order',
                undefined,
                metadataName,
                order.tenantId
              );
              setMetadata(metadata);
              setSavedWorkTypeId(
                typeof order.workTypeId === 'number' ? order.workTypeId : null
              );
              setOrderedEntityType(
                order.orderedEntityType ?? order.entityType ?? ''
              );
              setWorkFormDefinitionId(order.orderedFormDefinitionId);
              setWorkMetadataId(order.orderedEntityMetadataId);
              savedWorkForm.current = {
                key: order.orderedMetadataName,
                metadataId: order.orderedEntityMetadataId,
              };
              setOrderTenantId(
                order.tenantId ?? getTenantIdFromToken() ?? undefined
              );
              setWorkTypeReady(true);
              return order;
            }}
            saveDisabled={
              companyInfoErrors.length > 0 || !assetForms.canProceed
            }
            newAction={async (abortController, newOrder) => {
              if (!assetForms.canProceed) throw new Error(assetForms.message);
              const otherInfoErrors = validate(
                otherInfo,
                metadata?.validations ?? [],
                metadata?.fields
              );
              if (otherInfoErrors.length > 0) {
                setErrors(otherInfoErrors);
                return;
              }
              const data: ICreateOrderRequest = {
                selectedEntityMetadataId: metadata?.id,
                ...(newOrder.orderedEntityType === 'Work'
                  ? {
                      orderedFormDefinitionId: workFormDefinitionId,
                      orderedEntityMetadataId: workMetadataId,
                    }
                  : {}),
                siteAvailabilityWindows: siteAvailabilityRequests(
                  siteAvailabilityWindows
                ),
                description: newOrder.description,
                specialInstruction: textOrNull(newOrder.specialInstruction),
                code: newOrder.code,
                statusId: newOrder.statusId
                  ? Number.parseInt(newOrder.statusId)
                  : undefined,
                metadataName: `Order|${metadataName}`,
                orderedEntityType: newOrder.orderedEntityType,
                orderedMetadataName: newOrder.orderedMetadataName,
                relatedAssetIds: relatedAssetIds,
                otherInfo,
                ...(newOrder.orderedEntityType === 'Work'
                  ? { workTypeId }
                  : {}),
              };
              try {
                const newOrderResponse = await createOrder(
                  abortController,
                  data
                );
                setCurrentOrderId(newOrderResponse.orderId);
                initialRelatedAssetIdsRef.current = relatedAssetIds;
                setSuccessMessage(
                  `Order with id: ${newOrderResponse.orderId} created.`
                );
              } catch (error) {
                setErrorMessage(
                  (error as { message?: string })?.message ||
                    'Create order failed.'
                );
                throw error;
              }
            }}
            editAction={async (abortController, id, editedOrder) => {
              if (!assetForms.canProceed) throw new Error(assetForms.message);
              const otherInfoErrors = validate(
                otherInfo,
                metadata?.validations ?? [],
                metadata?.fields
              );
              if (otherInfoErrors.length > 0) {
                setErrors(otherInfoErrors);
                return;
              }
              const data: IUpdateOrderRequest = {
                ...(editedOrder.orderedEntityType === 'Work'
                  ? {
                      orderedFormDefinitionId: workFormDefinitionId,
                      orderedEntityMetadataId: workMetadataId,
                    }
                  : {}),
                code: editedOrder.code,
                description: editedOrder.description,
                specialInstruction: textOrNull(editedOrder.specialInstruction),
                orderedEntityType: editedOrder.orderedEntityType,
                orderedMetadataName: editedOrder.orderedMetadataName,
                relatedAssetIds: relatedAssetIds,
                otherInfo,
                statusId: editedOrder.statusId
                  ? Number.parseInt(editedOrder.statusId)
                  : undefined,
              };
              await editOrder(abortController, id, data);
              setSuccessMessage(`Order with id: ${id} successfully updated.`);
            }}
            isSaved={isSaved}
            isDirtyExternal={
              hasDraftRelatedAssetsChanges ||
              (!currentOrderId && siteAvailabilityWindows.length > 0) ||
              (!currentOrderId && workTypeId != null)
            }
            back={'/orders'}
            postButtons={[
              {
                text: 'Create related entity',
                icon: { iconName: 'AddTo' },
                onClick: async () => {
                  if (!assetForms.canProceed) {
                    setErrorMessage(
                      assetForms.message ?? 'Review the selected assets.'
                    );
                    return;
                  }
                  if (!currentOrderId) {
                    setErrorMessage(
                      'Save Order before creating related entity.'
                    );
                    return;
                  }
                  const abortController = new AbortController();
                  try {
                    const result = await createOrderedEntity(
                      abortController,
                      currentOrderId
                    );
                    setSuccessMessage(
                      `Created related entity with id: ${result.entityId} of type ${result.entityType}.`
                    );
                  } catch (error) {
                    setErrorMessage(
                      (error as { message?: string })?.message ||
                        'Could not create the related entity.'
                    );
                  }
                },
              },
            ]}
            afterChange={async (newValue: any) => {
              const entity =
                newValue.orderedEntityType ?? newValue.entityType ?? '';
              setOrderedEntityType(entity);
              if (
                Object.prototype.hasOwnProperty.call(
                  newValue,
                  'orderedMetadataName'
                )
              ) {
                setWorkFormDefinitionId(
                  formIdentities.current[newValue.orderedMetadataName]
                );
                setWorkMetadataId(
                  newValue.orderedMetadataName === savedWorkForm.current.key
                    ? savedWorkForm.current.metadataId
                    : workVersions.current[newValue.orderedMetadataName]
                );
              }
              if (!currentOrderId && entity !== 'Work') setWorkTypeId(null);
              setOrderedEntitiesMetadatas(
                filterOrderedMetadatas(allEntitiesMetadatas ?? [], entity)
              );
              if (newValue.hasOwnProperty('metadataName')) {
                const metadataName = newValue.metadataName.split('|')[1];
                setMetadataName(metadataName);
                const abortController = new AbortController();
                const metadata = await getMetadata(
                  abortController,
                  'Order',
                  undefined,
                  metadataName,
                  orderTenantId
                );
                setMetadata(metadata);
              }
            }}
            metadata={{
              fields: [
                ...(hasCodeConvention && !isEditMode
                  ? []
                  : [
                      {
                        name: 'code',
                        fieldType: 'String',
                        label: 'Code',
                        readonly: hasCodeConvention && isEditMode,
                      },
                    ]),
                {
                  name: 'description',
                  fieldType: 'String',
                  label: 'Description',
                },
                {
                  name: 'specialInstruction',
                  fieldType: 'String',
                  label: 'Special instruction',
                  rows: 3,
                },
                {
                  name: 'statusId',
                  fieldType: 'Lookup',
                  label: 'Status',
                  lookupList: 'Statuses',
                },
                {
                  name: 'metadataName',
                  fieldType: 'Lookup',
                  label: 'Order form',
                  lookupList: 'metadatas',
                  effect: currentOrderId ? 'Hide' : 'Show',
                },
                {
                  name: 'orderedEntityType',
                  fieldType: 'Lookup',
                  label: 'Order entity',
                  lookupList: 'entityTypes',
                },
                {
                  name: 'orderedMetadataName',
                  fieldType: 'Lookup',
                  label: 'Work/Asset form',
                  lookupList: 'orderedEntitiesMetadatas',
                },
                {
                  name: 'isTemplate',
                  fieldType: 'Checkbox',
                  label: 'Is Template',
                },
              ],
              lookups: [
                {
                  name: 'Statuses',
                  values: selectableOrderStatuses(
                    orderStatuses,
                    savedStatusId
                  ).map((s) => ({
                    key: s.id.toString(),
                    value: s.name,
                  })),
                },
                {
                  name: 'metadatas',
                  values: metadatas,
                },
                {
                  name: 'entityTypes',
                  values: [
                    { key: 'Work', value: 'Work' },
                    { key: 'Asset', value: 'Asset' },
                  ],
                },
                {
                  name: 'orderedEntitiesMetadatas',
                  values: orderedEntitiesMetadatas,
                },
              ],
              validations: [],
              interactions: [],
            }}
          >
            <>
              <OrderSiteAvailability
                key={currentOrderId ?? 'new'}
                orderId={currentOrderId || undefined}
                value={siteAvailabilityWindows}
                onChange={setSiteAvailabilityWindows}
                source="Portal"
              />
              {metadata ? (
                <OtherInfo
                  metadata={metadata}
                  otherInfo={JSON.stringify(otherInfo)}
                  isSaved={isSaved}
                  onOtherInfoChanged={(newValue: any) => setOtherInfo(newValue)}
                  validationErrors={errors}
                  onValidationErrorsChanged={(newErrors) =>
                    setErrors(newErrors)
                  }
                />
              ) : (
                <></>
              )}
            </>
          </EditItem>
        </PivotItem>
        <PivotItem
          headerText="Assets"
          headerButtonProps={disabledAssetsTabProps}
          onRenderItemLink={
            isAssetsTabDisabled
              ? (link, defaultRenderer) => (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    {defaultRenderer && link
                      ? defaultRenderer(link)
                      : link?.headerText}
                    <TooltipHost content={assetsTabTooltip}>
                      <FontIcon iconName="Info" style={{ color: '#605e5c' }} />
                    </TooltipHost>
                  </span>
                )
              : undefined
          }
        >
          <OrderAssets
            onAssetFormResolved={(assetId, formId) =>
              setKnownAssetForms((current) => ({
                ...current,
                [assetId]: formId,
              }))
            }
            assetForms={assetForms}
            tenantId={orderTenantId}
            orderId={currentOrderId}
            assetIds={relatedAssetIds}
            onAssetIdsChanged={setRelatedAssetIds}
          />
        </PivotItem>
        {currentOrderId && (
          <PivotItem headerText="Final billing">
            <OrderBilling key={currentOrderId} orderId={currentOrderId} />
          </PivotItem>
        )}
      </Pivot>
    </>
  );
};

export default Order;
